import axios, { Canceler } from 'axios';
import React from 'react';

import { Link } from '@dndbeyond/character-common-components/es';
import { LoadingPlaceholder } from '@dndbeyond/character-components/es';
import {
    ApiAdapterPromise,
    ApiAdapterRequestConfig,
    ApiAdapterUtils,
    ApiResponse,
    EntitledEntity,
    RacialTraitDefinitionContract,
    DefinitionPool,
    DefinitionPoolUtils,
    Constants,
    DefinitionUtils,
    RacialTrait,
    RacialTraitUtils,
    OptionalOrigin,
    Race,
    RaceUtils,
    OptionalOriginUtils,
    OptionalOriginLookup,
    HelperUtils,
    RuleData,
} from '@dndbeyond/character-rules-engine/es';

import DataLoadingStatusEnum from '../../../../../Shared/constants/DataLoadingStatusEnum';
import { AppLoggerUtils, TypeScriptUtils } from '../../../../../Shared/utils';
import { OptionalFeature } from '../../../../components/OptionalFeature';
import { AffectedFeatureInfo } from '../../../../components/OptionalFeature/OptionalFeature';
import { PageSubHeader } from '../../../../components/PageSubHeader';

interface OptionalOriginManagerProps {
    race: Race;
    definitionPool: DefinitionPool;
    optionalOriginLookup: OptionalOriginLookup;
    loadAvailableOptionalRacialTraits?: (
        additionalConfig?: Partial<ApiAdapterRequestConfig>,
    ) => ApiAdapterPromise<ApiResponse<EntitledEntity<RacialTraitDefinitionContract>>>;
    onDefinitionsLoaded?: (definitionData: EntitledEntity<RacialTraitDefinitionContract>) => void;
    onSelection: (definitionKey: string, affectedRacialTraitDefinitionKey: string | null) => void;
    onChangeReplacementPromise?: (
        definitionKey: string,
        newAffectedDefinitionKey: string | null,
        oldAffectedDefinitionKey: string | null,
        accept: () => void,
        reject: () => void,
    ) => void;
    onRemoveSelectionPromise?: (
        definitionKey: string,
        newIsEnabled: boolean,
        accept: () => void,
        reject: () => void,
    ) => void;
    ruleData: RuleData;
}
interface OptionalOriginManagerState {
    loadingStatus: DataLoadingStatusEnum;
}
export class OptionalOriginManager extends React.PureComponent<OptionalOriginManagerProps, OptionalOriginManagerState> {
    loadOptionalOriginsCanceler: null | Canceler = null;

    constructor(props: OptionalOriginManagerProps) {
        super(props);

        this.state = {
            loadingStatus: DataLoadingStatusEnum.NOT_LOADED,
        };
    }

    componentDidMount() {
        const { loadAvailableOptionalRacialTraits, onDefinitionsLoaded } = this.props;

        const { loadingStatus } = this.state;

        if (loadAvailableOptionalRacialTraits && loadingStatus === DataLoadingStatusEnum.NOT_LOADED) {
            this.setState({
                loadingStatus: DataLoadingStatusEnum.LOADING,
            });

            loadAvailableOptionalRacialTraits({
                cancelToken: new axios.CancelToken((c) => {
                    this.loadOptionalOriginsCanceler = c;
                }),
            })
                .then((response) => {
                    let data = ApiAdapterUtils.getResponseData(response);
                    if (data?.definitionData.length && onDefinitionsLoaded) {
                        onDefinitionsLoaded(data);
                    }

                    this.setState({
                        loadingStatus: DataLoadingStatusEnum.LOADED,
                    });
                    this.loadOptionalOriginsCanceler = null;
                })
                .catch(AppLoggerUtils.handleAdhocApiError);
        } else {
            this.setState({
                loadingStatus: DataLoadingStatusEnum.LOADED,
            });
        }
    }

    componentWillUnmount(): void {
        if (this.loadOptionalOriginsCanceler !== null) {
            this.loadOptionalOriginsCanceler();
        }
    }

    getAffectedFeatureInfos = (origin: RacialTrait): Array<AffectedFeatureInfo> => {
        const { definitionPool, race } = this.props;

        return RacialTraitUtils.getAffectedFeatureDefinitionKeys(origin)
            .map((definitionKey: string) => RacialTraitUtils.simulateRacialTrait(definitionKey, definitionPool))
            .filter(TypeScriptUtils.isNotNullOrUndefined)
            .map((affectedRacialTrait: RacialTrait) => {
                const definitionKey = RacialTraitUtils.getDefinitionKey(affectedRacialTrait);
                let disabled = RaceUtils.getOptionalOrigins(race).some(
                    (originMapping: OptionalOrigin) =>
                        OptionalOriginUtils.getDefinitionKey(originMapping) !==
                            RacialTraitUtils.getDefinitionKey(origin) &&
                        OptionalOriginUtils.getAffectedRacialTraitDefinitionKey(originMapping) === definitionKey,
                );

                return {
                    name: RacialTraitUtils.getName(affectedRacialTrait),
                    definitionKey,
                    disabled,
                };
            });
    };

    renderOptionalOriginCta = (): React.ReactNode => {
        return (
            <div className="ct-optional-origin-manager__content">
                <div className="ct-optional-origin-manager__content--empty">
                    <span>You currently have no available custom origins for this character</span>
                    <div className="ct-character-tools__marketplace-callout">
                        Unlock all official options in the <Link url="/marketplace">Marketplace</Link> or create them
                        for <Link url="/my-creations">Homebrew</Link> Races.
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { loadingStatus } = this.state;
        const {
            definitionPool,
            race,
            optionalOriginLookup,
            onChangeReplacementPromise,
            onRemoveSelectionPromise,
            onSelection,
        } = this.props;

        let contentNode: React.ReactNode;
        if (loadingStatus !== DataLoadingStatusEnum.LOADED) {
            contentNode = <LoadingPlaceholder />;
        } else {
            let availableOptionalOrigins: Array<RacialTrait> = DefinitionPoolUtils.getTypedDefinitionList(
                Constants.DefinitionTypeEnum.RACIAL_TRAIT,
                definitionPool,
            )
                .map((racialTraitDef) =>
                    RacialTraitUtils.simulateRacialTrait(
                        DefinitionUtils.getDefinitionKey(racialTraitDef),
                        definitionPool,
                    ),
                )
                .filter(TypeScriptUtils.isNotNullOrUndefined)
                .filter((racialTrait) => RacialTraitUtils.isValidRaceRacialTrait(race, racialTrait))
                .filter(
                    (racialTrait) => RacialTraitUtils.getFeatureType(racialTrait) !== Constants.FeatureTypeEnum.GRANTED,
                );

            let availableOptionalOriginLookup = HelperUtils.generateNonNullLookup(
                availableOptionalOrigins,
                RacialTraitUtils.getDefinitionKey,
            );

            const unEntitledOptionalRacialTraits: Array<RacialTrait> = [];
            Object.keys(optionalOriginLookup).forEach((definitionKey) => {
                if (!availableOptionalOriginLookup.hasOwnProperty(definitionKey)) {
                    const racialTrait = RacialTraitUtils.simulateRacialTrait(definitionKey, definitionPool);
                    if (racialTrait && RacialTraitUtils.isValidRaceRacialTrait(race, racialTrait)) {
                        unEntitledOptionalRacialTraits.push(racialTrait);
                    }
                }
            });

            const consolidatedOptionalOrigins: Array<RacialTrait> = [
                ...availableOptionalOrigins,
                ...unEntitledOptionalRacialTraits,
            ].filter(
                (racialTrait) =>
                    !RacialTraitUtils.deriveHideInContext(racialTrait, Constants.AppContextTypeEnum.BUILDER),
            );

            if (!consolidatedOptionalOrigins.length) {
                contentNode = this.renderOptionalOriginCta();
            } else {
                const replacementOrigins: Array<RacialTrait> = [];
                const additionalOrigins: Array<RacialTrait> = [];
                consolidatedOptionalOrigins.forEach((racialTrait) => {
                    switch (RacialTraitUtils.getFeatureType(racialTrait)) {
                        case Constants.FeatureTypeEnum.ADDITIONAL:
                            additionalOrigins.push(racialTrait);
                            break;
                        case Constants.FeatureTypeEnum.REPLACEMENT:
                            replacementOrigins.push(racialTrait);
                            break;
                        default:
                        //not implemented
                    }
                });

                contentNode = (
                    <div className="ct-optional-origin-manager__content">
                        {replacementOrigins.length > 0 && (
                            <React.Fragment>
                                <PageSubHeader>Replacement Traits</PageSubHeader>
                                {RaceUtils.deriveOrderedRacialTraits(replacementOrigins).map((origin: RacialTrait) => {
                                    const optionalOriginMapping = HelperUtils.lookupDataOrFallback(
                                        optionalOriginLookup,
                                        RacialTraitUtils.getDefinitionKey(origin),
                                    );
                                    const affectedFeatureDefinitionKey = optionalOriginMapping
                                        ? OptionalOriginUtils.getAffectedRacialTraitDefinitionKey(optionalOriginMapping)
                                        : null;

                                    return (
                                        <OptionalFeature
                                            key={RacialTraitUtils.getDefinitionKey(origin)}
                                            name={`Origin ${RacialTraitUtils.getName(origin)}`}
                                            description={RacialTraitUtils.getDescription(origin)}
                                            featureType={RacialTraitUtils.getFeatureType(origin)}
                                            definitionKey={RacialTraitUtils.getDefinitionKey(origin)}
                                            affectedFeatureDefinitionKey={affectedFeatureDefinitionKey}
                                            isSelected={!!optionalOriginMapping ?? false}
                                            onSelection={onSelection}
                                            onChangeReplacementPromise={onChangeReplacementPromise}
                                            onRemoveSelectionPromise={onRemoveSelectionPromise}
                                            affectedFeatures={this.getAffectedFeatureInfos(origin)}
                                            accessType={RacialTraitUtils.getAccessType(origin)}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        )}
                        {additionalOrigins.length > 0 && (
                            <React.Fragment>
                                <PageSubHeader>Additional Traits</PageSubHeader>
                                {RaceUtils.deriveOrderedRacialTraits(additionalOrigins).map((origin: RacialTrait) => {
                                    const optionalOriginMapping = HelperUtils.lookupDataOrFallback(
                                        optionalOriginLookup,
                                        RacialTraitUtils.getDefinitionKey(origin),
                                    );
                                    const affectedFeatureDefinitionKey = optionalOriginMapping
                                        ? OptionalOriginUtils.getAffectedRacialTraitDefinitionKey(optionalOriginMapping)
                                        : null;

                                    return (
                                        <OptionalFeature
                                            key={RacialTraitUtils.getDefinitionKey(origin)}
                                            name={RacialTraitUtils.getName(origin)}
                                            description={RacialTraitUtils.getDescription(origin)}
                                            featureType={RacialTraitUtils.getFeatureType(origin)}
                                            definitionKey={RacialTraitUtils.getDefinitionKey(origin)}
                                            affectedFeatureDefinitionKey={affectedFeatureDefinitionKey}
                                            isSelected={!!optionalOriginMapping ?? false}
                                            onSelection={onSelection}
                                            onChangeReplacementPromise={onChangeReplacementPromise}
                                            onRemoveSelectionPromise={onRemoveSelectionPromise}
                                            affectedFeatures={this.getAffectedFeatureInfos(origin)}
                                            accessType={RacialTraitUtils.getAccessType(origin)}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        )}
                    </div>
                );
            }
        }

        return (
            <div className="ct-optional-origin-manager">
                <div className="ct-optional-origin-manager__intro">
                    Character race in the game represents your character’s fantasy species, combined with certain
                    cultural assumptions. The following options step outside those assumptions to pave the way for truly
                    unique characters.
                </div>
                {contentNode}
            </div>
        );
    }
}

export default OptionalOriginManager;
